import { Badge, Button, HStack, Td, Text, Tr, useDisclosure, VStack, useColorModeValue, useTheme } from '@chakra-ui/react';
import { AbsencePeriod, AbsenceRecord } from '../../types';
import { AbsenceStatusBadge, absenceStatusColorMap } from './AbsenceStatusBadge';
import { AbsenceTypeBadge } from './AbsenceTypeBadge';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { TimeRangeVisualizer } from '../common/DateRangeVisualizer';
import { useSuIntl } from '../../hooks/useSuIntl';
import { useAuthStore } from '../../store/auth/authStore';
import UserPolicy from '../../policies/UserPolicy';
import { AbsenceReviewModal } from './AbsenceReviewModal';
import { useIntl } from 'react-intl';
import { AbsenceReviewStatusBadge } from './AbsenceReviewStatusBadge';

type AbsenceTableItemProps = {
    item: AbsenceRecord;
    onClick: () => void;
    onReviewSuccess?: () => void;
};

export const AbsenceTableItem: React.FC<AbsenceTableItemProps> = ({ item, onClick, onReviewSuccess }) => {
    const { formatDate } = useSuIntl();
    const intl = useIntl();
    const theme = useTheme();
    const loggedUser = useAuthStore((s) => s.user);
    const reviewModal = useDisclosure();

    const canReview = loggedUser && UserPolicy.canReviewAbsence(loggedUser, item);

    return (
        <Tr
            role="group"
            _hover={{
                bg: useColorModeValue(theme.colors.gray[200], theme.colors.gray[600]),
            }}
            cursor={'pointer'}
            onClick={onClick}
            bg={useColorModeValue('white', 'gray.700')}
        >
            <Td borderLeftRadius={'lg'} borderLeftWidth={4} borderLeftColor={absenceStatusColorMap[item.status]}>
                <VStack alignItems="start" spacing="1">
                    <Text fontWeight={600} fontSize="medium">
                        {item.user?.surname} {item.user?.firstName}
                    </Text>
                    <AbsenceTypeBadge type={item.type} size="sm" />
                </VStack>
            </Td>
            <Td>
                <HStack spacing={2}>
                    <TimeRangeVisualizer dateFrom={formatDate(item.dateFrom)} dateTo={formatDate(item.dateTo)} />
                    {item.period === AbsencePeriod.HALF_DAY && (
                        <Badge colorScheme="orange" fontSize="xs">
                            {intl.formatMessage({ id: 'absence.halfDay' })}
                        </Badge>
                    )}
                </HStack>
            </Td>
            <Td>
                <AbsenceStatusBadge status={item.status} />
            </Td>
            <Td borderRightRadius={'lg'} width="100px">
                <HStack justifyContent={'flex-end'} spacing={2}>
                    {canReview ? (
                        <Button
                            size="xs"
                            colorScheme="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                reviewModal.onOpen();
                            }}
                        >
                            {intl.formatMessage({ id: 'absence.review' })}
                        </Button>
                    ) : (
                        <AbsenceReviewStatusBadge date={item.reviewedAt || item.createdAt} status={item.status} />
                    )}
                    <ChevronRightIcon boxSize={5} display={{ base: 'none', md: 'block' }} />
                </HStack>
            </Td>
            <AbsenceReviewModal isOpen={reviewModal.isOpen} onClose={reviewModal.onClose} absenceId={item.id} onSuccess={onReviewSuccess} />
        </Tr>
    );
};
